import { AlertTriangle, X } from './icons.tsx'
import {
  useCallback,
  useEffect,
  useEffectEvent,
  useId,
  useRef,
  useState,
  type ReactNode,
} from 'react'
import { createPortal } from 'react-dom'
import { Button } from './button.tsx'
import { cx } from './cx.ts'
import { motionReduced } from './motion.tsx'
import { FloatHost } from './anchored.tsx'
import { IconTile, type Area } from './layout.tsx'

export interface ModalProps {
  open: boolean
  onClose: () => void
  title: ReactNode
  children: ReactNode
  footer?: ReactNode
  /** `full` covers the whole window: something to turn towards a customer. */
  size?: 'sm' | 'md' | 'lg' | 'xl' | 'wide' | 'full'
  /**
   * The area a main job belongs to, wherever it was opened from: tints the header. Warnings,
   * tools, pickers and small edits have none (DESIGN.md, Modal).
   */
  area?: Area
  /** With `area`: drawn on the area's tile before the title. */
  icon?: ReactNode
  /** One plain line under the title; not part of the dialog's name. */
  subtitle?: string
  /**
   * What would be lost, while the dialog holds typed work ("5 lines not received yet"). A tap
   * on the backdrop then only nudges the dialog and shows these words; X, Escape and the
   * caller's own buttons still close it. Left out, the dialog notices typing by itself; pass
   * `false` where typing is only a search (a picker, a print dialog).
   */
  unsaved?: string | false
  /** Must be answered with the dialog's own buttons: no X, and Escape and the backdrop do nothing. */
  required?: boolean
}

// Spelled out so Tailwind sees every class.
const AREA_HEADER: Record<Area, string> = {
  sell: 'bg-sell-wash',
  people: 'bg-people-wash',
  stock: 'bg-stock-wash',
  buy: 'bg-buy-wash',
  insight: 'bg-insight-wash',
  kitchen: 'bg-kitchen-wash',
  neutral: 'bg-neutral-wash',
  take: 'bg-take-wash',
}

const SIZES = {
  sm: 'max-w-sm',
  md: 'max-w-lg',
  lg: 'max-w-2xl',
  xl: 'max-w-4xl',
  wide: 'max-w-6xl',
  full: 'h-dvh',
}

/** How long the words of a refused backdrop tap stay in the footer. */
const UNSAVED_MS = 4000

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'

/** Makes everything else on the page inert while `layer` is open; returns the undo. */
function isolate(layer: HTMLElement): () => void {
  const others = [...document.body.children].filter(
    (el): el is HTMLElement =>
      el instanceof HTMLElement &&
      el !== layer &&
      !el.inert &&
      !el.hasAttribute('data-stay-interactive'),
  )
  for (const el of others) el.inert = true
  return () => {
    for (const el of others) el.inert = false
  }
}

export function Modal({
  open,
  onClose,
  title,
  children,
  footer,
  size = 'md',
  area,
  icon,
  subtitle,
  unsaved,
  required = false,
}: ModalProps) {
  const layer = useRef<HTMLDivElement>(null)
  const panel = useRef<HTMLDivElement | null>(null)
  // The panel, for lists that float over the dialog (AnchoredList renders into it).
  const [panelEl, setPanelEl] = useState<HTMLDivElement | null>(null)
  const panelRef = useCallback((el: HTMLDivElement | null) => {
    panel.current = el
    setPanelEl(el)
  }, [])
  const titleId = useId()
  // X, Escape and the backdrop play the closing motion first; the caller's own buttons
  // (Cancel, Save) close at once.
  const [leaving, setLeaving] = useState(false)
  const done = useRef(false)
  const finish = () => {
    if (done.current) return
    done.current = true
    onClose()
  }
  const dismiss = () => {
    if (leaving) return
    done.current = false
    setLeaving(true)
    // animationend ends it; the timer covers a browser that skips the animation.
    window.setTimeout(finish, 220)
  }
  // A stray tap outside a dialog with typed work in it: a short shake says "still here", and
  // the footer says what is not saved. Nothing closes.
  const [warned, setWarned] = useState(false)
  const [typed, setTyped] = useState(false)
  const words = unsaved ?? (typed && 'What you typed is not saved yet')
  const warnTimer = useRef(0)
  const backdrop = () => {
    if (required) return
    if (!words) return dismiss()
    if (!motionReduced())
      panel.current?.animate(
        [
          { transform: 'translateX(0)' },
          { transform: 'translateX(-6px)' },
          { transform: 'translateX(5px)' },
          { transform: 'translateX(-3px)' },
          { transform: 'translateX(0)' },
        ],
        { duration: 300, easing: 'ease-out' },
      )
    setWarned(true)
    window.clearTimeout(warnTimer.current)
    warnTimer.current = window.setTimeout(() => setWarned(false), UNSAVED_MS)
  }
  useEffect(() => () => window.clearTimeout(warnTimer.current), [])
  // Callers often pass an inline onClose: it must not re-run the open/close effect.
  const close = useEffectEvent(() => {
    if (!required) dismiss()
  })

  useEffect(() => {
    if (!open) return
    const previous = document.activeElement as HTMLElement | null
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close()
      // Keep Tab inside the dialog.
      if (e.key === 'Tab' && panel.current) {
        const items = [...panel.current.querySelectorAll<HTMLElement>(FOCUSABLE)]
        const first = items[0]
        const last = items[items.length - 1]
        if (!first || !last) return
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault()
          last.focus()
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault()
          first.focus()
        }
      }
    }
    document.addEventListener('keydown', onKey)
    const restore = layer.current ? isolate(layer.current) : () => {}
    // Focus the first field (or the panel) for keyboard users.
    const first = panel.current?.querySelector<HTMLElement>(
      'input, select, textarea, button:not([aria-label="Close"])',
    )
    // On a touch screen a focused field raises the keyboard over the dialog, so the
    // dialog itself takes focus there.
    const touch = window.matchMedia('(pointer: coarse)').matches
    ;((touch ? null : first) ?? panel.current)?.focus()
    return () => {
      document.removeEventListener('keydown', onKey)
      restore()
      previous?.focus()
    }
  }, [open])

  // Opened again after closing: start from rest.
  const [wasOpen, setWasOpen] = useState(open)
  if (open !== wasOpen) {
    setWasOpen(open)
    if (open) {
      setLeaving(false)
      setTyped(false)
      setWarned(false)
    }
  }
  if (!open) return null
  return createPortal(
    <div
      ref={layer}
      className={cx(
        'fixed inset-0 z-50 flex items-end justify-center bg-black/40 p-0',
        size !== 'full' && 'sm:items-center sm:p-4',
        leaving && 'pointer-events-none animate-dim-out',
      )}
      onMouseDown={backdrop}
    >
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        onMouseDown={(e) => e.stopPropagation()}
        onInput={() => {
          if (!typed) setTyped(true)
        }}
        onAnimationEnd={(e) => {
          if (leaving && e.target === e.currentTarget) finish()
        }}
        className={cx(
          'flex w-full flex-col bg-raised',
          leaving ? 'animate-scale-out' : 'animate-scale-in',
          size === 'full'
            ? ''
            : 'max-h-[92vh] rounded-t-card border border-border shadow-modal sm:rounded-card',
          SIZES[size],
        )}
      >
        <div
          data-area={area}
          className={cx(
            'flex items-center gap-3 border-b border-border px-5 py-4',
            // The panel can't clip it (lists float out of the panel), so a tint follows its corners.
            size !== 'full' && 'rounded-t-card',
            area && AREA_HEADER[area],
          )}
        >
          {area && icon && <IconTile area={area}>{icon}</IconTile>}
          <div className="min-w-0 flex-1">
            <h2 id={titleId} className="text-lg font-semibold">
              {title}
            </h2>
            {subtitle && <p className="text-sm text-muted">{subtitle}</p>}
          </div>
          {!required && <CloseButton onClick={dismiss} />}
        </div>
        <div
          className={cx(
            'overflow-y-auto overscroll-contain px-5 py-4',
            size === 'full' && 'flex-1',
          )}
        >
          <FloatHost.Provider value={panelEl}>{children}</FloatHost.Provider>
        </div>
        {(footer || words) && (
          <div className="flex flex-wrap items-center justify-end gap-2 border-t border-border px-5 py-3 empty:hidden">
            {words && (
              <p aria-live="polite" className={warned ? 'mr-auto w-full' : 'sr-only'}>
                {warned && (
                  <span className="inline-flex items-center gap-1.5 rounded-full bg-warning-light px-2.5 py-1 text-sm font-semibold text-warning">
                    <AlertTriangle aria-hidden className="size-4" />
                    {words}. Press Cancel to leave.
                  </span>
                )}
              </p>
            )}
            {footer}
          </div>
        )}
      </div>
    </div>,
    document.body,
  )
}

/**
 * A dialog's or drawer's X: a real button, square and outlined, whose cross turns a quarter
 * while it is pointed at, and presses in when tapped.
 */
export function CloseButton({
  onClick,
  label = 'Close',
  className,
}: {
  onClick: () => void
  label?: string
  className?: string
}) {
  return (
    <button
      type="button"
      aria-label={label}
      onClick={onClick}
      className={cx(
        'group tap-target grid size-9 shrink-0 place-items-center rounded-xl border border-border bg-surface text-muted transition-[background-color,color,transform] duration-150 ease-out hover:bg-soft hover:text-ink active:scale-95 focus-visible:text-ink',
        className,
      )}
    >
      <X className="size-[18px] transition-transform duration-200 ease-out group-hover:rotate-90 group-focus-visible:rotate-90" />
    </button>
  )
}

export function ConfirmDialog({
  open,
  title,
  message,
  confirmLabel = 'Confirm',
  danger = false,
  busy = false,
  onConfirm,
  onClose,
  children,
}: {
  open: boolean
  title: string
  message?: ReactNode
  confirmLabel?: string
  danger?: boolean
  busy?: boolean
  onConfirm: () => void
  onClose: () => void
  children?: ReactNode
}) {
  return (
    <Modal
      open={open}
      onClose={onClose}
      title={title}
      size="sm"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button variant={danger ? 'danger' : 'primary'} loading={busy} onClick={onConfirm}>
            {confirmLabel}
          </Button>
        </>
      }
    >
      {message && <p className="text-sm text-muted">{message}</p>}
      {children}
    </Modal>
  )
}
